import { _ } from "libs";
import React, { useState, useLayoutEffect } from "react";
import moment from "moment-timezone";
import cx from "classnames";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
type OnChange = (date: string) => void;

const nullOp = () => {};

const GCDayPicker = ({
	onChange,
	value,
	daysAhead = 14,
	timezone,
	debounce = 300,
}: {
	onChange: OnChange;
	value: string;
	daysAhead?: number;
	timezone: string;
	debounce?: number;
}) => {
	const [subject$, setSubject] = useState(null as Subject<number>);

	useLayoutEffect(() => {
		const subject$ = new Subject<number>();

		const subscription = subject$.pipe(debounceTime(debounce)).subscribe(index => {
			const day = getDay(index);
			setDate({ day, index });
			onChange(day);
		});

		setSubject(subject$);

		return () => {
			subscription.unsubscribe();
		};
	}, []);

	const todayObject = moment.tz(timezone).startOf("day");

	const today = todayObject.unix();

	let selectedIndex = 0;
	let days = [];

	for (let index = 0; index < daysAhead; index++) {
		const day = todayObject.clone();
		day.add(index, "d");

		const date = day.format("YYYY-MM-DD");
		let title = day.format("ddd, D MMM");

		if (index === 0) {
			title = _("today");
		} else if (index === 1) {
			title = _("tomorrow");
		}

		if (value === date) {
			selectedIndex = index;
		}

		days.push({ date, title });
	}

	const [date, setDate] = useState({ day: value || today, index: selectedIndex });

	const getTitle = (index: number): string => {
		const day = days[index];
		return day.title;
	};

	const getDay = (index: number): string => {
		const day = days[index];
		return day.date;
	};

	const onDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const index = parseInt(e.target.value);
		const day = getDay(index);
		setDate({ day, index });
		onChange(day);
	};

	const prevDay = () => {
		let { index } = date;
		index = index - 1;

		const day = getDay(index);
		setDate({ day, index });

		subject$.next(index);
	};

	const nextDay = () => {
		let { index } = date;
		index = index + 1;

		const day = getDay(index);
		setDate({ day, index });

		subject$.next(index);
	};

	const prevDisabled = date.index === 0;
	const nextDisabled = date.index === daysAhead - 1;

	return (
		<div className="dayPicker">
			<div className={cx("prev", { disabled: prevDisabled })} onClick={!prevDisabled ? prevDay : nullOp}>
				<div className="icon" />
			</div>
			<div className="current">
				<div className="value">{getTitle(date.index)}</div>
				<select onChange={onDateChange} value={date.index}>
					{days.map((day, index) => (
						<option key={index} value={index}>
							{day.title}
						</option>
					))}
				</select>
			</div>
			<div className={cx("next", { disabled: nextDisabled })} onClick={!nextDisabled ? nextDay : nullOp}>
				<div className="icon" />
			</div>
		</div>
	);
};

export default GCDayPicker;
